import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Camera, ArrowRight, Fingerprint, Mail, MessageCircle, KeyRound } from "lucide-react";
import { startAuthentication, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import AuthLayout, { authStyles } from "@/components/AuthLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingGoogle, setProcessingGoogle] = useState(false);
  const [processingApple, setProcessingApple] = useState(false);
  const [processingPasskey, setProcessingPasskey] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);

  // Contact email modal state (for Apple Private Relay users)
  const [showContactEmailModal, setShowContactEmailModal] = useState(false);
  const [contactEmail, setContactEmail] = useState("");
  const [pendingUserData, setPendingUserData] = useState(null);
  const [pendingSessionToken, setPendingSessionToken] = useState(null);
  const [savingContactEmail, setSavingContactEmail] = useState(false);

  // Check if passkeys are supported
  useEffect(() => {
    setPasskeySupported(browserSupportsWebAuthn());
  }, []);

  // Show message if redirected due to expired session or OAuth error
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      toast.error('Your session has expired. Please log in again.');
      window.history.replaceState({}, document.title, '/login');
    }
    if (searchParams.get('error') === 'account_suspended') {
      toast.error("We're having trouble with your account. Please contact support@shootzu.com", { duration: 6000 });
      window.history.replaceState({}, document.title, '/login');
    }
  }, [searchParams]);

  // Check for session_token in URL fragment (from Google/Apple OAuth redirect)
  useEffect(() => {
    const processOAuthCallback = async () => {
      const hash = window.location.hash;
      console.log('🔵 Checking URL hash:', hash);

      if (hash && hash.includes('session_token=')) {
        setProcessingGoogle(true);
        console.log('🔵 Session token found in URL');

        // Extract session_token from URL fragment
        const sessionToken = hash.split('session_token=')[1].split('&')[0];
        console.log('🔵 Extracted token:', sessionToken.substring(0, 20) + '...');
        // JWT refresh token so the 7-day session can be renewed (absent on old backends)
        const oauthRefreshToken = new URLSearchParams(hash.slice(1)).get('refresh_token');

        // Check if needs_contact_email is in the URL
        const needsContactEmail = hash.includes('needs_contact_email=true');
        console.log('🔵 Needs contact email:', needsContactEmail);

        try {
          console.log('🔵 Calling /auth/me with token...');
          // Get user data with session token
          const response = await axios.get(
            `${API}/auth/me`,
            {
              headers: { 'Authorization': `Bearer ${sessionToken}` }
            }
          );

          const userData = response.data;
          console.log('✅ User data received:', userData.email);

          // Clean URL fragment
          window.history.replaceState({}, document.title, window.location.pathname);

          // App Store Guideline 4 (Sign in with Apple): do NOT force users to
          // re-provide their email after Apple sign-in. The private-relay address
          // forwards to the user, so we proceed straight in.

          // Store user data with correct keys
          localStorage.setItem('shootzu_user', JSON.stringify(userData));
          localStorage.setItem('shootzu_token', sessionToken);
          console.log('✅ User data stored in localStorage');

          toast.success(`Welcome ${userData.full_name || userData.email}!`);

          // Call onLogin and navigate
          console.log('🔵 Calling onLogin callback...');
          onLogin(sessionToken, userData, oauthRefreshToken);

          // Redirect based on user status
          const userStatus = (userData.status || '').toUpperCase();
          if (userStatus === 'NEW') {
            console.log('🔵 NEW user - Navigating to onboarding...');
            navigate('/onboarding');
          } else if (userStatus === 'PENDING_APPROVAL') {
            console.log('🔵 PENDING_APPROVAL user - Navigating to pending-approval...');
            navigate('/pending-approval');
          } else {
            console.log('🔵 Navigating to dashboard...');
            navigate('/dashboard');
          }

        } catch (error) {
          console.error('❌ Google auth error:', error);
          console.error('❌ Error response:', error.response?.data);
          toast.error(
            error.response?.data?.detail || 'Google sign-in failed. Please try again.'
          );
          // Clean URL fragment on error
          window.history.replaceState({}, document.title, window.location.pathname);
        } finally {
          setProcessingGoogle(false);
        }
      } else {
        console.log('🔵 No session token in URL hash');
      }
    };

    processOAuthCallback();
  }, [navigate, onLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });

      onLogin(response.data.token, response.data.user, response.data.refresh_token || null);

      // Redirect based on user status
      const userStatus = (response.data.user.status || '').toUpperCase();
      if (userStatus === 'NEW') {
        console.log('🔵 NEW user - Navigating to onboarding...');
        navigate("/onboarding");
      } else if (userStatus === 'PENDING_APPROVAL') {
        console.log('🔵 PENDING_APPROVAL user - Navigating to pending-approval...');
        navigate("/pending-approval");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      // Check if rate limited
      if (error.response?.status === 429) {
        toast.error("Too many attempts, please wait 15 minutes", { duration: 6000 });
      } else {
        const errorDetail = error.response?.data?.detail || "Login failed. Please try again.";

        // Check if account is suspended
        if (errorDetail.includes("suspended") || errorDetail.includes("Suspended") ||
            error.response?.data?.user?.account_status === "suspended" ||
            error.response?.data?.user?.approval_status === "suspended") {
          toast.error(
            "We're having trouble with your account. Please contact support@shootzu.com",
            { duration: 6000 }
          );
        } else {
          toast.error(errorDetail);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Get Google OAuth URL from backend
      const response = await axios.get(`${API}/auth/google/login`);
      const authUrl = response.data.auth_url;

      // Redirect to Google
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to initiate Google login:', error);
      toast.error('Failed to connect to Google. Please try again.');
    }
  };

  const handleAppleLogin = async () => {
    try {
      // Get Apple OAuth URL from backend
      const response = await axios.get(`${API}/auth/apple/login`);
      const authUrl = response.data.auth_url;

      // Check if authUrl exists before redirecting
      if (!authUrl) {
        toast.error('Apple Sign In is not available yet.');
        return;
      }

      // Redirect to Apple
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to initiate Apple login:', error);
      if (error.response?.status === 500 && error.response?.data?.detail?.includes('not configured')) {
        toast.error('Apple Sign In is not available yet.');
      } else {
        toast.error('Failed to connect to Apple. Please try again.');
      }
    }
  };

  const handlePasskeyLogin = async () => {
    if (!passkeySupported) {
      toast.error('Passkeys are not supported on this device.');
      return;
    }

    setProcessingPasskey(true);
    try {
      // Get authentication options from backend (no email = show all available passkeys)
      const optionsResponse = await axios.post(`${API}/auth/passkey/login/options`, {});
      const options = optionsResponse.data;

      // Trigger passkey authentication
      const credential = await startAuthentication({ optionsJSON: options });

      // Verify with backend
      const verifyResponse = await axios.post(`${API}/auth/passkey/login/verify`, {
        credential: credential
      });

      const { user, tokens } = verifyResponse.data;

      // Normalize camelCase fields to snake_case for consistency with /auth/me
      if (user.firstName && !user.first_name) user.first_name = user.firstName;
      if (user.lastName && !user.last_name) user.last_name = user.lastName;
      if (user.fullName && !user.full_name) user.full_name = user.fullName;
      if (user.profilePhotoUrl && !user.profile_photo_url) user.profile_photo_url = user.profilePhotoUrl;
      if (user.accountType && !user.account_type) user.account_type = user.accountType;
      if (user.userNumber && !user.user_number) user.user_number = user.userNumber;

      // Store tokens and user data
      localStorage.setItem('shootzu_token', tokens.accessToken);
      localStorage.setItem('shootzu_refresh_token', tokens.refreshToken);
      localStorage.setItem('shootzu_user', JSON.stringify(user));

      toast.success(`Welcome back, ${user.fullName || user.email}!`);

      // Call onLogin callback
      onLogin(tokens.accessToken, user, tokens.refreshToken || null);

      // Navigate based on user status
      const userStatus = (user.status || '').toUpperCase();
      if (userStatus === 'NEW') {
        navigate('/onboarding');
      } else if (userStatus === 'PENDING_APPROVAL') {
        navigate('/pending-approval');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Passkey login error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Authentication was cancelled.');
      } else if (error.response?.status === 404) {
        toast.error('No passkeys found. Please use another login method.');
      } else {
        toast.error(error.response?.data?.detail || 'Passkey login failed. Please try again.');
      }
    } finally {
      setProcessingPasskey(false);
    }
  };

  // Handle contact email submission for Apple Private Relay users
  const handleContactEmailSubmit = async (e) => {
    e.preventDefault();
    if (!contactEmail.trim() || !pendingUserData || !pendingSessionToken) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Don't allow another Apple relay email
    if (contactEmail.toLowerCase().includes('privaterelay.appleid.com')) {
      toast.error('Please provide a regular email address, not an Apple Private Relay address');
      return;
    }

    setSavingContactEmail(true);
    try {
      // Save contact email to backend
      await axios.post(
        `${API}/users/${pendingUserData.id}/contact-email`,
        { contact_email: contactEmail },
        { headers: { 'Authorization': `Bearer ${pendingSessionToken}` } }
      );

      // Update user data with contact email
      const updatedUserData = { ...pendingUserData, contact_email: contactEmail };

      // Store user data and proceed with login
      localStorage.setItem('shootzu_user', JSON.stringify(updatedUserData));
      localStorage.setItem('shootzu_token', pendingSessionToken);

      toast.success(`Welcome ${updatedUserData.full_name || updatedUserData.email}!`);

      // Close modal and clear state
      setShowContactEmailModal(false);
      setContactEmail('');
      setPendingUserData(null);
      setPendingSessionToken(null);

      // Call onLogin and navigate
      onLogin(pendingSessionToken, updatedUserData);

      // Redirect based on user status
      const userStatus = (updatedUserData.status || '').toUpperCase();
      if (userStatus === 'NEW') {
        navigate('/onboarding');
      } else if (userStatus === 'PENDING_APPROVAL') {
        navigate('/pending-approval');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to save contact email:', error);
      toast.error(error.response?.data?.detail || 'Failed to save email. Please try again.');
    } finally {
      setSavingContactEmail(false);
    }
  };

  // Show loading spinner if processing Google auth
  if (processingGoogle) {
    return (
      <AuthLayout>
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mb-4" style={{ borderTopColor: "transparent", borderColor: "#28C0D0", borderTopWidth: 4 }}></div>
          <p style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}>Completing sign-in…</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* Heading */}
      <div className="auth-head" style={authStyles.heading}>Welcome back</div>
      <div className="auth-head" style={{ ...authStyles.sub, marginBottom: 24 }}>Sign in to your Shootzu account.</div>

      {/* Social / passkey stack */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 8 }}>
        <button
          type="button"
          onClick={handleGoogleLogin}
          className="auth-social"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 13, borderRadius: 12, background: "#fff", border: "none", color: "#1f2430", fontFamily: "'Work Sans', sans-serif", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 12 1 11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 4.75 12 4.75z"/>
          </svg>
          Continue with Google
        </button>
        <button
          type="button"
          onClick={handleAppleLogin}
          className="auth-social"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 13, borderRadius: 12, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.16)", color: "#fff", fontFamily: "'Work Sans', sans-serif", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff"><path d="M17.05 12.6c-.03-2.7 2.2-4 2.3-4.06-1.25-1.84-3.2-2.09-3.9-2.12-1.66-.17-3.24.98-4.08.98-.84 0-2.14-.96-3.52-.93-1.81.03-3.48 1.05-4.41 2.67-1.88 3.27-.48 8.1 1.35 10.76.9 1.3 1.97 2.76 3.38 2.71 1.36-.05 1.87-.88 3.51-.88 1.64 0 2.1.88 3.53.85 1.46-.03 2.38-1.33 3.27-2.63 1.03-1.51 1.46-2.97 1.48-3.05-.03-.01-2.84-1.09-2.87-4.32M14.4 4.68c.74-.9 1.24-2.15 1.1-3.4-1.07.05-2.36.71-3.13 1.61-.68.79-1.29 2.06-1.13 3.27 1.2.09 2.42-.6 3.16-1.48"/></svg>
          Continue with Apple
        </button>
        {passkeySupported && (
          <button
            type="button"
            onClick={handlePasskeyLogin}
            disabled={processingPasskey}
            className="auth-social"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 13, borderRadius: 12, background: "rgba(40,192,208,.1)", border: "1px solid rgba(40,192,208,.3)", color: "#28C0D0", fontFamily: "'Work Sans', sans-serif", fontWeight: 600, fontSize: 14, cursor: "pointer", opacity: processingPasskey ? 0.6 : 1 }}
          >
            {processingPasskey ? (
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-t-transparent" style={{ borderColor: "#28C0D0", borderTopColor: "transparent" }}></div>
            ) : (
              <KeyRound className="w-[17px] h-[17px]" />
            )}
            Sign in with passkey
          </button>
        )}
      </div>

      {/* divider */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0" }}>
        <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,.1)" }} />
        <span style={{ fontSize: 11, color: "rgba(255,255,255,.4)" }}>or</span>
        <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,.1)" }} />
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        <div>
          <span style={authStyles.label}>Email</span>
          <input
            className="auth-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            style={authStyles.input}
            required
          />
        </div>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: "11.5px", color: "rgba(255,255,255,.5)" }}>Password</span>
            <Link to="/forgot-password" className="auth-link" style={{ fontSize: "11.5px" }}>Forgot?</Link>
          </div>
          <div style={{ position: "relative" }}>
            <input
              className="auth-input"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              style={{ ...authStyles.input, paddingRight: 44 }}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "rgba(255,255,255,.45)", cursor: "pointer", display: "flex" }}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <button type="submit" disabled={loading} className="auth-primary" style={{ ...authStyles.primaryBtn, marginTop: 4, opacity: loading ? 0.7 : 1 }}>
          {loading ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-t-transparent" style={{ borderColor: "#0C1120", borderTopColor: "transparent" }}></span>
              Signing in…
            </span>
          ) : "Sign in"}
        </button>
      </form>

      <div style={{ textAlign: "center", fontSize: 13, color: "rgba(255,255,255,.55)", marginTop: 20 }}>
        New here? <Link to="/signup" className="auth-link" style={{ fontWeight: 600 }}>Create an account</Link>
      </div>

      {/* Footer */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 24, fontSize: 12, color: "rgba(255,255,255,.4)" }}>
        <button onClick={() => window.TurboChat?.toggle()} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "inherit", cursor: "pointer" }}>
          <MessageCircle className="w-3.5 h-3.5" />
          Support
        </button>
        <span>·</span>
        <span>© {new Date().getFullYear()} Shootzu by Turbo 360</span>
      </div>

      {/* Contact Email Modal for Apple Private Relay Users */}
      {showContactEmailModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-[#1e1534] border-gray-700 rounded-2xl shadow-2xl">
            <CardHeader className="border-b border-gray-700 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-600/20 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg" style={{ fontFamily: 'Space Grotesk' }}>
                    One More Step
                  </CardTitle>
                  <p className="text-sm text-white/50 mt-1">Add your contact email</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-white/60 text-sm mb-6">
                Since you signed in with Apple using a private email, please provide your real email address so we can send you important notifications about jobs and leads.
              </p>
              <form onSubmit={handleContactEmailSubmit}>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="contactEmail" className="text-white/70 text-xs font-medium mb-2 block">
                      Your Email Address
                    </Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="h-11 bg-white/[0.07] border-white/[0.12] text-white placeholder:text-white/40 focus:bg-white/[0.12] focus:border-[#7B3FF2] rounded-xl transition-all"
                      required
                      autoFocus
                    />
                    <p className="text-xs text-white/40 mt-2">
                      This email will be used to send you job alerts and important notifications.
                    </p>
                  </div>
                  <Button
                    type="submit"
                    disabled={savingContactEmail || !contactEmail.trim()}
                    className="w-full h-11 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl shadow-lg transition-all duration-200"
                  >
                    {savingContactEmail ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      'Continue'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </AuthLayout>
  );
};

export default Login;
